import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { useLoggedInUser, useFeatureFlags, useProductAssetsByUser, useUser } from '@tcc/shared-data-lib';
import { initializeAnalytics } from '@tcc/analytics';
import { AlertToaster, useBreakpoint } from '@tcc/fds';
import { ReportsPluginContainer } from '../containers/ReportsPluginContainer';
import { ConnectPluginContainer } from '../containers/ConnectPluginContainer';
import { DmbPluginContainer } from '../containers/DmbPluginContainer';
import { VariableRatePluginContainer } from '../containers/VariableRateContainer';
import { CropPlanningPluginContainer } from '../containers/CropPlanningPluginContainer';
import { ProgramsPluginContainer } from '../containers/ProgramsPluginContainer';
import { DashboardPluginContainer } from '../containers/DashboardPluginContainer';
import { InfoArchitecturePluginContainer } from '../containers/InfoArchitecturePluginContainer';
import { AskFieldviewWebPluginContainer } from '../containers/AskFieldviewWebPluginContainer';
import { ModuleFederationPluginContainer } from '../containers/ModuleFederationPluginContainer';
import { FarmerNav } from '../components/FarmerNav/FarmerNav';
import { UnverifiedBlocker } from '../components/UnverifiedBlocker';
import { useOperations } from '../hooks/useOperations/useOperations';
import { useHistoryListener } from '../hooks/useHistoryListener';
import { useSessionSettings } from '../hooks/useSessionSettings';
import { FieldSelectionPluginContainer } from '../containers/FieldSelectionContainer';
import { SpinnerLoading } from '../components/SpinnerLoading/SpinnerLoading';
import { getUserData, reAuthenticate } from '../utils/wam';
import { ErrorFallback } from '../components/ErrorFallback';
import { DataInboxPluginContainer } from '../containers/DataInboxPluginContainer';
import { FixitToolPluginContainer } from '../containers/FixitToolPluginContainer';
import { DataHubPluginContainer } from '../containers/DataHubPluginContainer';

import styles from './styles.css';
import { AuthorizedRoute } from './AuthorizedRoute';
import { ROUTE } from '../constants/routes';
import { SessionSetting } from '../constants/ui';
import { useFeatureFlagsConfigs, useProductAssetsByUserConfigs, useUserLoggedInConfigs } from '../utils/sharedData';
import { getAvailableFeatures } from '../utils/getAvailableFeatures';
import { ScriptCreatorPluginContainer } from '../containers/ScriptCreatorContainer';
import { queryParam } from '../utils/queryString';
import { ProxyBanner } from '../components/ProxyBanner/ProxyBanner';
import { SettingsPluginContainer } from '../containers/SettingsPluginContainer/SettingsPluginContainer';
import { useMarketingNotifications } from '../hooks/useMarketingNotifications';
import { useCobrowsingSupport } from '../hooks/useCobrowsingSupport';
import { InSeasonPluginContainer } from '../containers/InSeasonContainer';
import { OperationHubPluginContainer } from '../containers/OperationHubPluginContainer';
import { useExpertoBanner } from '../hooks/useExpertoBanner';
import { ExpertoDialog } from '../components/ExpertoDialog/ExpertoDialog';

export const ApplicationView = () => {
  const { clientId } = getUserData();
  const location = useLocation();
  const {
    operations,
    operationsSelected,
    setSelectedOperations,
  } = useOperations(clientId);

  const { isDesktop } = useBreakpoint();
  const { sessionSettings, updateSessionSetting } = useSessionSettings();
  const [pluginContainerElement, setPluginContainerElement] = useState<HTMLDivElement | null>(null);

  const getInitialExpandedState = () => {
    const storedValue = sessionSettings[SessionSetting.SidenavExpanded];
    return storedValue !== null ? storedValue : isDesktop;
  };

  const [expanded, setExpandedState] = useState(getInitialExpandedState);

  const setExpanded = (value: boolean) => {
    setExpandedState(value);
    updateSessionSetting(SessionSetting.SidenavExpanded, value);
  };

  const [isExpertoDialogOpen, setIsExpertoDialogOpen] = useState(false);
  const handleOpenExpertoDialog = useCallback(() => setIsExpertoDialogOpen(true), []);
  const handleCloseExpertoDialog = useCallback(() => setIsExpertoDialogOpen(false), []);
  const handlePluginContainerRef = useCallback((node: HTMLDivElement | null) => {
    setPluginContainerElement(node);
  }, []);

  const { user } = useLoggedInUser(useUserLoggedInConfigs);
  const impersonatedUserId = queryParam('client-id');
  const loggedInUserId = user?.id?.toString() || '';
  const userId = impersonatedUserId || loggedInUserId;
  const { user: impersonatedUser } = useUser({
    config: { skip: !user || !impersonatedUserId || loggedInUserId === impersonatedUserId },
    params: { legacyUserId: userId },
  });
  const { featureFlags, isLoading: isLoadingFeatureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const { data: assets, isLoading: isLoadingAssets } = useProductAssetsByUser(useProductAssetsByUserConfigs(user));

  useEffect(() => {
    if (user && user.details) {
      initializeAnalytics({ userDetails: user.details, featureFlags });
    }
  }, [featureFlags, user]);

  // TODO: remove useHistoryListener prop after FEWEB-1305
  useHistoryListener(featureFlags?.['web.analytics.monitoring']);
  useMarketingNotifications();
  useCobrowsingSupport();
  const { handleConfirmHideExpertoBanner } = useExpertoBanner({
    user,
    featureFlags: featureFlags || {},
    containerElement: pluginContainerElement,
    onOpenDialog: handleOpenExpertoDialog,
    onCloseDialog: handleCloseExpertoDialog,
    isDialogOpen: isExpertoDialogOpen,
  });

  const operationsSelectedIds = useMemo(() => operationsSelected.map((op) => op.operationId), [operationsSelected]);

  const blockUser = user?.details?.emailVerificationStatus === 'UNVERIFIED';
  if (blockUser) {
    return <UnverifiedBlocker onLogout={reAuthenticate} timeout={5000} />;
  }

  const isMissingRequiredData = !user || !assets || !featureFlags;
  const isSharedDataLoading = isLoadingAssets || isLoadingFeatureFlags;

  if (isMissingRequiredData || isSharedDataLoading) {
    return <SpinnerLoading />;
  }

  const availableFeatures = getAvailableFeatures({ user, assets, featureFlags, impersonatedUser });
  const Home = availableFeatures.dashboard ? ROUTE.Dashboard : ROUTE.Home;

  const includeSideNav = availableFeatures.informationArchitecture;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <main className={`${styles.mainContainer} ${includeSideNav && styles.mainContainerRow}`}>
        {!includeSideNav && <FarmerNav
          operations={operations}
          operationsSelected={operationsSelected}
          setSelectedOperations={setSelectedOperations}
        />}
        {includeSideNav && <ProxyBanner />}
        {includeSideNav &&
          <InfoArchitecturePluginContainer
            expanded={expanded}
            operations={operations}
            operationsSelected={operationsSelected}
            setExpandedState={setExpanded}
            setSelectedOperations={setSelectedOperations}
          />}
        <div className={styles.pluginWrapper}>
          {!isExpertoDialogOpen && <AlertToaster />}
          <div className={styles.pluginContainer} ref={handlePluginContainerRef}>
            <Suspense fallback={<SpinnerLoading />}>
              <ExpertoDialog
                isOpen={isExpertoDialogOpen}
                onClose={handleCloseExpertoDialog}
                onOpenChange={setIsExpertoDialogOpen}
                onPrimaryAction={handleConfirmHideExpertoBanner}
              />
              <Switch>
                <AuthorizedRoute
                  isEnabled={availableFeatures.reports}
                  path={ROUTE.Reports}
                  render={() => (
                    <ReportsPluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <Route path={ROUTE.DataInbox} render={() => <DataInboxPluginContainer operationsSelected={operationsSelected} />} />
                <Route path={ROUTE.FixitTool} render={() => <FixitToolPluginContainer operationsSelected={operationsSelected} />} />
                <Route
                  path={ROUTE.Dmb}
                  render={() => (
                    <DmbPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )} />
                <Route
                  path={ROUTE.VariableRate}
                  render={() => (
                    <VariableRatePluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )} />
                <Route
                  path={ROUTE.FieldSelection}
                  render={() => (
                    <section className={styles.flexFSWrapper}>
                      <FieldSelectionPluginContainer
                        operationsSelected={operationsSelected}
                      />
                    </section>
                  )}
                />
                <Route path={ROUTE.ScriptCreator} render={() => (
                  <section>
                    <ScriptCreatorPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  </section>
                )} />
                <Route path={ROUTE.Settings} render={() => (
                  <SettingsPluginContainer
                    operations={operations}
                    operationsSelected={operationsSelectedIds}
                  />
                )} />
                <Route path={ROUTE.ModuleFederation} render={() => (
                  <ModuleFederationPluginContainer />
                )} />
                <AuthorizedRoute
                  isEnabled={availableFeatures.programs}
                  path={ROUTE.Programs}
                  component={ProgramsPluginContainer}
                />
                {/* TODO ADO-12609: Remove this redirect - data-hub was never released */}
                <Route exact path="/data-hub">
                  <Redirect to="/analytics" />
                </Route>
                <AuthorizedRoute
                  isEnabled={availableFeatures.analytics}
                  path={ROUTE.DataHub}
                  render={() => (
                    <DataHubPluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.dashboard}
                  path={ROUTE.Dashboard}
                  render={() => (
                    <DashboardPluginContainer operations={operations} />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.connect}
                  path={ROUTE.Partners}
                  component={ConnectPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.cropPlanning}
                  path={ROUTE.CropPlanning}
                  component={CropPlanningPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.askFieldView}
                  path={ROUTE.AskFieldView}
                  component={AskFieldviewWebPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.inSeason}
                  path={ROUTE.InSeason}
                  render={() => (
                    <InSeasonPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.operationHub}
                  path={ROUTE.OperationHub}
                  component={OperationHubPluginContainer}
                />
                <Route path="*">
                  <Redirect to={{
                    ...location,
                    pathname: Home
                  }} />
                </Route>
              </Switch>
            </Suspense>
          </div>
        </div>
      </main>
    </ErrorBoundary>
  );
};
